// sentry.config.ts
import * as Sentry from '@sentry/nextjs';

import packageInfo from './package.json';

type SentryInitOptions = Parameters<typeof Sentry.init>[0];

const projectName = 'breitling-storefront-next';
const isProd = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === 'production';

// --- Feature flags (toggles) ---
const SENTRY_ENABLED =
  (process.env.NEXT_PUBLIC_SENTRY_ENABLED ?? 'true') === 'true';

// SDK-side error sampling (0..1). Ej: 0.25 = 25% de errores en prod
const ERROR_SAMPLE_RATE = Number(
  process.env.NEXT_PUBLIC_SENTRY_ERROR_SAMPLE_RATE ?? (isProd ? '0.25' : '1'),
);

// Per-session/minute guard rails (token bucket súper simple)
const MAX_EVENTS_PER_MINUTE = Number(
  process.env.NEXT_PUBLIC_SENTRY_MAX_EVENTS_PER_MINUTE ?? '30',
);
const MAX_EVENTS_PER_SESSION = Number(
  process.env.NEXT_PUBLIC_SENTRY_MAX_EVENTS_PER_SESSION ?? '100',
);

// Toggle para capturar console.error como eventos
const CAPTURE_CONSOLE =
  (process.env.NEXT_PUBLIC_SENTRY_CAPTURE_CONSOLE ?? 'false') === 'true';

// Allowlist de orígenes propios (reduce ruido de terceros/extensiones)
const ALLOW_URLS = [
  /https?:\/\/([a-z0-9-]+\.)?breitling\.com/i,
  /https?:\/\/([a-z0-9-]+\.)?gallet\.com/i,
  /https?:\/\/.*\.vercel\.app/i,
];

// --- Local limiter state (por sesión de pestaña) ---
let minuteCount = 0;
let sessionCount = 0;
let minuteWindow = Date.now();
function underBudget() {
  const now = Date.now();
  if (now - minuteWindow > 60_000) {
    minuteWindow = now;
    minuteCount = 0;
  }
  return (
    minuteCount < MAX_EVENTS_PER_MINUTE && sessionCount < MAX_EVENTS_PER_SESSION
  );
}
function bumpBudget() {
  minuteCount++;
  sessionCount++;
}

export const sentryInitDefaults: SentryInitOptions = {
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN, // usa aquí tu DSN "capado" si quieres hard-cap
  enabled: SENTRY_ENABLED && !!process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT_NAME,
  release: `${projectName}@${packageInfo.version}`,
  debug: JSON.parse(process.env.NEXT_PUBLIC_SENTRY_DEBUG || 'false'),

  // Error-event sampling (no confundir con tracesSampleRate)
  sampleRate: ERROR_SAMPLE_RATE,

  integrations: [
    Sentry.reportingObserverIntegration(),
    ...(CAPTURE_CONSOLE
      ? [Sentry.captureConsoleIntegration({ levels: ['error'] })]
      : []),
    // Sólo en navegador para evitar errores en SSR/Edge
    ...(typeof window !== 'undefined'
      ? [
          Sentry.browserTracingIntegration({
            tracePropagationTargets: [
              /^\/api\//,
              /^https?:\/\/(?!gtm\.)[^/]*breitling\.com/i,
              /^https?:\/\/[^/]*gallet\.com/i,
            ],
          }),
        ]
      : []),
  ],

  // Performance: baja si necesitas reducir aún más ingestión
  tracesSampleRate: isProd ? 0.05 : 1.0,
  profilesSampleRate: isProd ? 0.05 : 1.0,

  // Filtrado por origen de código
  allowUrls: ALLOW_URLS,

  normalizeDepth: 10,
  attachStacktrace: true,
  maxBreadcrumbs: 50,
  ignoreErrors: [
    'Sentry error ID',
    /ResizeObserver loop limit exceeded/i,
    /AbortError/i,
    /Non-Error promise rejection captured/i,
    /NetworkError when attempting to fetch resource/i,
    /Loading chunk \d+ failed/i,
    /chunk load error/i,
  ],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /chrome-extension:\/\//i,
    /safari-web-extension:/i,
  ],

  sendDefaultPii: typeof window === 'undefined',

  beforeBreadcrumb(b: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
    // Evita navegación muy ruidosa
    if (b.category === 'navigation') return null;
    return b;
  },

  beforeSend(event: Sentry.Event, hint: Sentry.EventHint): Sentry.Event | null {
    // Per-minute / per-session guard
    if (!underBudget()) return null;

    // Si es un evento sólo de console.message (sin exception), puedes cortar aquí si quieres
    const isConsoleOnly = !event.exception && !!event.message;
    if (isConsoleOnly && !CAPTURE_CONSOLE) return null;

    // Noisy chunk/network errors (doble cortafuegos)
    const msg =
      typeof hint?.originalException === 'string'
        ? hint?.originalException
        : (hint?.originalException as any)?.message;
    if (
      typeof msg === 'string' &&
      (/ResizeObserver loop limit exceeded/i.test(msg) ||
        /Loading chunk \d+ failed/i.test(msg) ||
        /chunk load error/i.test(msg))
    ) {
      return null;
    }

    bumpBudget();
    return event;
  },
};
